/**
 * Lumina Cart — AJAX cart drawer management
 * Depends on: lumina-utils.js (window.Lumina.utils, window.Lumina.toast)
 *             snippets/cart-drawer.liquid renders #lumina-cart-i18n + #cart-upsell-data
 *             sections/cart-drawer.liquid wraps the snippet for Section Rendering API
 */
(function () {
  'use strict';

  // ── Locale-aware route helper ────────────────────────────────────────────
  // Shopify auto-populates Shopify.routes.root with the active storefront
  // locale prefix (e.g. "/", "/ar/", "/fr-fr/"). Bare "/cart.js" returns
  // titles in the SHOP'S DEFAULT language, so always go through localUrl().
  function localUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + String(path).replace(/^\//, '');
  }

  // ── i18n bridge ──────────────────────────────────────────────────────────
  // cart-drawer.liquid renders <script id="lumina-cart-i18n">{...}</script>
  // with all translated strings. We read it once and expose tt() for both
  // this file and the inline drawer script.
  function getI18N() {
    if (window.LUMINA_CART_I18N) return window.LUMINA_CART_I18N;
    try {
      var el = document.getElementById('lumina-cart-i18n');
      if (el) {
        window.LUMINA_CART_I18N = JSON.parse(el.textContent);
        return window.LUMINA_CART_I18N;
      }
    } catch (e) { /* ignore */ }
    return {};
  }

  function tt(key, replacements) {
    if (window.LUMINA_CART_T) return window.LUMINA_CART_T(key, replacements);
    var i18n = getI18N();
    var str = i18n[key] || '';
    if (replacements) {
      Object.keys(replacements).forEach(function (k) {
        str = str.split('__' + k.toUpperCase() + '__').join(replacements[k]);
      });
    }
    return str;
  }

  var CartManager = {
    drawer:      null,
    countEls:    [],
    bodyEl:      null,
    _refreshing: false,
    _pendingNextQueue: null,
    _upsellSourceQueue: null,
    _sectionSwapped: false,
    _initialized: false,
    _qtyTimers:  {}, // per-line-key debounce timers for qty +/- coalescing

    init: function () {
      if (this._initialized) return;
      this._initialized = true;

      this.drawer   = document.querySelector('[data-cart-drawer]');
      this.countEls = document.querySelectorAll('[data-cart-count]');
      this.bodyEl   = document.querySelector('[data-cart-drawer-body]');

      // Open / close
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-trigger]')) {
          e.preventDefault();
          CartManager.open();
        }
        if (e.target.closest('[data-cart-close]')) CartManager.close();
      });

      document.addEventListener('keydown', function (e) {
        var isOpen = CartManager.drawer && CartManager.drawer.classList.contains('is-open');
        if (!isOpen) return;
        if (e.key === 'Escape') { CartManager.close(); return; }
        // Keep Tab inside the open drawer (it is aria-modal).
        if (e.key === 'Tab') {
          var panel = CartManager.drawer.querySelector('.cart-drawer__panel') || CartManager.drawer;
          var nodes = Array.prototype.filter.call(
            panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'),
            function (el) { return el.offsetParent !== null; }
          );
          if (!nodes.length) return;
          var first = nodes[0], last = nodes[nodes.length - 1];
          if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
            e.preventDefault(); last.focus();
          } else if (!e.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
            e.preventDefault(); first.focus();
          }
        }
      });

      // External ATCs (PDP, etc.) dispatch this when the cart changes
      document.addEventListener('lumina:cart:updated', function () {
        if (!CartManager.drawer || !CartManager.drawer.classList.contains('is-open')) {
          CartManager.open();
        } else {
          CartManager.refresh();
        }
      });

      // Quantity changes
      document.addEventListener('click', function (e) {
        if (document.body.classList.contains('cart-is-loading')) {
          e.preventDefault();
          return;
        }

        var qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          var key     = qtyBtn.dataset.qtyChange;
          var delta   = parseInt(qtyBtn.dataset.qtyDelta, 10);
          var display = document.querySelector('[data-qty-display="' + key + '"]');
          var current = display ? parseInt(display.textContent, 10) : 1;
          var next    = Math.max(0, current + delta);

          // Optimistic UI: update displayed qty immediately so the user sees
          // instant feedback even if the network is slow. The server-rendered
          // section swap will overwrite with the authoritative value.
          if (display) display.textContent = next;
          qtyBtn.classList.add('is-loading');

          // Debounce: if the user mashes +/- rapidly, coalesce into one
          // request with the FINAL qty instead of N sequential round-trips.
          if (CartManager._qtyTimers[key]) clearTimeout(CartManager._qtyTimers[key]);
          CartManager._qtyTimers[key] = setTimeout(function () {
            delete CartManager._qtyTimers[key];
            var liveDisplay = document.querySelector('[data-qty-display="' + key + '"]');
            var finalQty    = liveDisplay ? parseInt(liveDisplay.textContent, 10) : next;
            CartManager.updateItem(key, finalQty);
          }, 280);
        }

        var removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          var rkey = removeBtn.dataset.removeItem;
          // If a debounced qty change is pending for this line, cancel it —
          // remove (qty=0) supersedes any pending qty bump.
          if (CartManager._qtyTimers[rkey]) {
            clearTimeout(CartManager._qtyTimers[rkey]);
            delete CartManager._qtyTimers[rkey];
          }
          removeBtn.classList.add('is-loading');
          CartManager.updateItem(rkey, 0);
        }
      });

      // Quick add (upsell, quick-view, etc.)
      document.addEventListener('click', function (e) {
        var qaBtn = e.target.closest('[data-quick-add]');
        if (qaBtn) {
          e.preventDefault();
          CartManager.addItem(qaBtn.dataset.quickAdd, 1, qaBtn).catch(function () {});
        }
      });

      // Clear cart
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-clear-cart]')) {
          e.preventDefault();
          CartManager.clearCart();
        }
      });
    },

    open: function () {
      if (!this.drawer) this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;
      var alreadyOpen = this.drawer.classList.contains('is-open');
      if (!alreadyOpen) this._returnFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      document.body.style.overflow = 'hidden';
      var firstBtn = this.drawer.querySelector('button, a');
      if (firstBtn && !alreadyOpen) setTimeout(function () { firstBtn.focus(); }, 300);
      if (!alreadyOpen && !this._skipNextRefresh) this.refresh();
      this._skipNextRefresh = false;
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      document.body.style.overflow = '';
      // Give focus back to whatever opened the drawer (cart icon, ATC button).
      var back = this._returnFocus;
      this._returnFocus = null;
      if (back && typeof back.focus === 'function' && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
      }
    },

    refresh: function () {
      if (this._refreshing) return;
      this._refreshing = true;

      // Used for initial drawer open and external triggers
      // (lumina:cart:updated). Mutations (add/change/clear) skip this and
      // process the section HTML returned from the mutation response itself.
      var url = localUrl('cart.js') + '?sections=cart-drawer';
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) { CartManager._processCartResponse(cart); })
        .catch(function (e) { console.warn('[Lumina] Cart refresh failed', e); })
        .finally(function () { CartManager._refreshing = false; });
    },

    // Single source of truth for applying a cart payload to the UI.
    // Called by refresh() AND directly by mutation responses (updateItem,
    // addItem, clearCart) — those mutations request ?sections=cart-drawer so
    // their own response carries everything needed to repaint the drawer
    // without a second round-trip.
    _processCartResponse: function (cart) {
      if (!cart || typeof cart !== 'object') return;
      this.updateCountDisplay(cart.item_count);
      this.renderItems(cart);
      if (!this._sectionSwapped) {
        this._updateShippingBar(cart.total_price);
      }
      this._sectionSwapped = false;
    },

    // Swap server-rendered HTML for parts of the drawer that contain
    // Liquid-translated strings: shipping bar, items list, footer subtotal.
    // Only inner content is replaced — the drawer wrapper, snippet <script>
    // and <style> blocks, and document-level event delegation all stay intact.
    _swapSection: function (sectionHtml) {
      if (!sectionHtml) return false;
      var parser = new DOMParser();
      var doc = parser.parseFromString(sectionHtml, 'text/html');

      var swapped = false;
      var pairs = [
        ['[data-cart-drawer-body]', '[data-cart-drawer-body]'],
        ['[data-cart-shipping-bar]', '[data-cart-shipping-bar]']
      ];
      pairs.forEach(function (pair) {
        var fresh = doc.querySelector(pair[0]);
        var live  = document.querySelector(pair[1]);
        if (fresh && live) {
          live.innerHTML = fresh.innerHTML;
          swapped = true;
        }
      });

      // Footer needs both content swap AND inline display-style sync
      // (snippet uses style="display:none" to hide it on empty cart).
      var freshFooter = doc.querySelector('[data-cart-footer]');
      var liveFooter  = document.querySelector('[data-cart-footer]');
      if (freshFooter && liveFooter) {
        liveFooter.innerHTML = freshFooter.innerHTML;
        liveFooter.setAttribute('style', freshFooter.getAttribute('style') || '');
        swapped = true;
      }

      // Re-sync upsell candidate JSON blob from the new render
      var freshUpsellData = doc.querySelector('#cart-upsell-data');
      var liveUpsellData  = document.querySelector('#cart-upsell-data');
      if (freshUpsellData && liveUpsellData) {
        liveUpsellData.textContent = freshUpsellData.textContent;
        if (freshUpsellData.dataset.heading) {
          liveUpsellData.dataset.heading = freshUpsellData.dataset.heading;
        }
      }

      // Sync clear button visibility (depends on cart count, server-rendered)
      var freshClear = doc.querySelector('.cart-drawer__clear');
      var liveClear  = document.querySelector('.cart-drawer__clear');
      if (freshClear && liveClear) {
        liveClear.hidden = freshClear.hasAttribute('hidden');
      }

      return swapped;
    },

    updateCountDisplay: function (count) {
      this.countEls.forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
      var drawerCount = document.querySelector('[data-cart-drawer-count]');
      if (drawerCount) drawerCount.textContent = '(' + count + ')';
    },

    // Extract the cart item_count from a freshly-rendered section HTML string
    // by reading [data-cart-drawer-count]'s "(N)" text. Used by addItem's
    // single-trip path where Shopify returns the line item (no item_count).
    _extractCount: function (sectionHtml) {
      if (!sectionHtml) return null;
      try {
        var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var el = doc.querySelector('[data-cart-drawer-count]');
        if (!el) return null;
        var m = el.textContent.match(/\d+/);
        return m ? parseInt(m[0], 10) : null;
      } catch (e) { return null; }
    },

    updateItem: function (key, quantity) {
      // Per-item visual loading state: dim the row, show spinner, disable
      // qty +/- and remove buttons. Cleared by the items list re-render.
      var itemEl = document.querySelector('[data-cart-item][data-item-key="' + key + '"]');
      if (itemEl) {
        itemEl.classList.add('is-loading');
        itemEl.querySelectorAll('button').forEach(function (b) {
          b.disabled = true;
          b.setAttribute('aria-busy', 'true');
        });
      }

      // ONE round-trip: change endpoint also returns the rendered section.
      // Eliminates the extra GET /cart.js?sections=... that refresh() did.
      fetch(localUrl('cart/change.js') + '?sections=cart-drawer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) throw data; // 422: quantity above stock, etc.
            return data;
          });
        })
        .then(function (cart) { CartManager._processCartResponse(cart); })
        .catch(function (e) {
          CartManager.showError(e);
          if (itemEl) {
            itemEl.classList.remove('is-loading');
            itemEl.querySelectorAll('button').forEach(function (b) {
              b.disabled = false;
              b.removeAttribute('aria-busy');
              b.classList.remove('is-loading');
            });
          }
          // Revert any optimistic UI (qty number was bumped client-side before
          // the request) by re-syncing from the server.
          CartManager.refresh();
          console.warn('[Lumina] Cart update failed', e);
        });
    },

    clearCart: function () {
      document.body.classList.add('cart-is-loading');
      fetch(localUrl('cart/clear.js') + '?sections=cart-drawer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.body.classList.remove('cart-is-loading');
          if (window.location.pathname.indexOf('/cart') !== -1) {
            window.location.reload();
            return;
          }
          CartManager._processCartResponse(cart);
        })
        .catch(function (e) {
          document.body.classList.remove('cart-is-loading');
          console.warn('[Lumina] Cart clear failed', e);
        });
    },

    addItem: function (variantId, quantity, triggerEl) {
      var nextQueue = null;
      if (triggerEl && triggerEl.dataset.nextQueue) {
        try { nextQueue = JSON.parse(triggerEl.dataset.nextQueue); }
        catch (e) { console.warn('[Lumina] Invalid nextQueue JSON', e); }
      }
      this._pendingNextQueue = nextQueue;
      var added = false; // true once Shopify accepted the add

      if (triggerEl) {
        triggerEl.disabled = true;
        triggerEl.classList.add('is-loading');
        triggerEl.setAttribute('aria-busy', 'true');
        if (!triggerEl.dataset.originalText) {
          triggerEl.dataset.originalText = triggerEl.textContent;
        }
      }

      // ONE round-trip: passing `sections: 'cart-drawer'` in the body makes
      // /cart/add.js return { ...lineItem, sections: { 'cart-drawer': '...' } }
      // so we can swap the rendered HTML directly — no follow-up GET /cart.js.
      return fetch(localUrl('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: variantId,
          quantity: quantity || 1,
          sections: 'cart-drawer'
        })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) throw data; // Shopify variant-unavailable etc.
            added = true;
            return data;
          });
        })
        .then(function (response) {
          // Open drawer if closed; suppress its built-in refresh — we have HTML.
          if (CartManager.drawer && !CartManager.drawer.classList.contains('is-open')) {
            CartManager._skipNextRefresh = true;
            CartManager.open();
          }

          var sectionHtml = response && response.sections && response.sections['cart-drawer'];
          if (sectionHtml && CartManager._swapSection(sectionHtml)) {
            // Sync count badges (header + external) from the freshly-rendered HTML.
            var newCount = CartManager._extractCount(sectionHtml);
            if (newCount !== null) CartManager.updateCountDisplay(newCount);
            CartManager._sectionSwapped = true;
            // Upsell candidates were filtered server-side against current cart_product_ids,
            // so updateUpsell only needs item_count to know whether to render at all.
            CartManager.updateUpsell({ items: [], item_count: newCount != null ? newCount : 1 });
            return;
          }

          // Fallback (older Shopify or unexpected response shape) — old 2-trip path.
          CartManager._refreshing = false;
          return fetch(localUrl('cart.js') + '?sections=cart-drawer', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) { CartManager._processCartResponse(cart); });
        })
        .then(function () {
          if (triggerEl) {
            triggerEl.disabled = false;
            triggerEl.classList.remove('is-loading');
            triggerEl.removeAttribute('aria-busy');
            triggerEl.textContent = tt('addedCheck') || 'Added ✓';
            var originalText = triggerEl.dataset.originalText || tt('addShort') || '+ Add';
            setTimeout(function () {
              if (document.contains(triggerEl)) triggerEl.textContent = originalText;
            }, 1800);
          }
          if (window.Lumina && window.Lumina.toast) {
            window.Lumina.toast.show(tt('addedToBag') || 'Item added to your bag', 'success');
          }
        })
        .catch(function (e) {
          if (added) {
            // The item IS in the cart; only the drawer repaint failed. Never
            // report this as a failed add (callers would retry and double-add).
            console.warn('[Lumina] Cart UI refresh after add failed', e);
            if (triggerEl) {
              triggerEl.disabled = false;
              triggerEl.classList.remove('is-loading');
              triggerEl.removeAttribute('aria-busy');
            }
            CartManager.refresh();
            return;
          }
          console.warn('[Lumina] Add to cart failed', e);
          CartManager.showError(e);
          if (triggerEl) {
            triggerEl.disabled = false;
            triggerEl.classList.remove('is-loading');
            triggerEl.removeAttribute('aria-busy');
            triggerEl.textContent = tt('tryAgain') || 'Try again';
            var restoreText = triggerEl.dataset.originalText;
            if (restoreText) {
              setTimeout(function () {
                if (document.contains(triggerEl)) triggerEl.textContent = restoreText;
              }, 2500);
            }
          }
          throw e;
        });
    },

    renderItems: function (cart) {
      var body = this.bodyEl || document.querySelector('[data-cart-drawer-body]');
      if (!body) return;
      this.bodyEl = body;

      var footer   = document.querySelector('[data-cart-footer]');
      var clearBtn = document.querySelector('.cart-drawer__clear');

      // Preferred path: Shopify rendered the cart-drawer section server-side
      // in the active locale. Swap that HTML in and skip the JS-built items
      // list entirely (which would otherwise use English titles from /cart.js).
      var sectionHtml = cart.sections && cart.sections['cart-drawer'];
      if (sectionHtml && CartManager._swapSection(sectionHtml)) {
        CartManager._sectionSwapped = true;
        if (cart.item_count === 0) CartManager._upsellSourceQueue = null;
        CartManager.updateUpsell(cart);
        return;
      }

      // ── Fallback: JS-built items list (loses Liquid-translated titles) ──
      if (cart.item_count === 0) {
        CartManager._upsellSourceQueue = null;
        body.innerHTML = CartManager._emptyStateHTML();
        if (footer) footer.style.display = 'none';
        if (clearBtn) clearBtn.hidden = true;
        return;
      } else {
        if (clearBtn) clearBtn.hidden = false;
      }

      if (footer) {
        footer.style.display = '';
        var itemsSubtotalEl = footer.querySelector('[data-cart-items-subtotal]');
        var subtotalEl      = footer.querySelector('[data-cart-subtotal]');
        var discountsEl     = footer.querySelector('[data-cart-discounts]');
        var checkoutBtn     = footer.querySelector('.cart-drawer__checkout');

        // Items subtotal (sum of line totals before cart-level discounts)
        if (itemsSubtotalEl) {
          var itemsSubtotal = (typeof cart.items_subtotal_price === 'number')
            ? cart.items_subtotal_price
            : cart.total_price;
          itemsSubtotalEl.textContent = CartManager._fmt(itemsSubtotal);
        }

        // Cart-level discount lines (rebuilt from /cart.js payload)
        if (discountsEl) {
          var apps = cart.cart_level_discount_applications || [];
          if (apps.length === 0) {
            discountsEl.innerHTML = '';
            discountsEl.hidden = true;
          } else {
            var dHtml = '';
            for (var di = 0; di < apps.length; di++) {
              var app = apps[di] || {};
              var titleSafe  = CartManager._escape(app.title || 'Discount');
              var amountFmt  = CartManager._fmt(app.total_allocated_amount || 0);
              dHtml += '<div class="cart-drawer__summary-row cart-drawer__summary-row--discount">'
                +   '<span class="cart-drawer__summary-label">'
                +     '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                +       '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>'
                +       '<line x1="7" y1="7" x2="7.01" y2="7"/>'
                +     '</svg> ' + titleSafe
                +   '</span>'
                +   '<span class="cart-drawer__summary-value cart-drawer__summary-value--discount">&minus;' + amountFmt + '</span>'
                + '</div>';
            }
            discountsEl.innerHTML = dHtml;
            discountsEl.hidden = false;
          }
        }

        // Delivery priced from the cart total (not the shipping zone). The row
        // carries its own fee/threshold from theme settings, so this path stays
        // in sync with the Liquid render when the section swap is unavailable.
        var grandTotal = cart.total_price;
        var deliveryRow = footer.querySelector('[data-cart-delivery-row]');
        if (deliveryRow) {
          var fee        = parseInt(deliveryRow.dataset.deliveryFee || '0', 10) || 0;
          var dThresh    = parseInt(deliveryRow.dataset.deliveryThreshold || '0', 10) || 0;
          var smallThr   = parseInt(deliveryRow.dataset.deliverySmallThreshold || '0', 10) || 0;
          var smallFee   = parseInt(deliveryRow.dataset.deliverySmallFee || '0', 10) || 0;
          // Same three tiers as the Liquid render, cheapest cart first.
          var dueNow;
          if (dThresh > 0 && cart.total_price >= dThresh) {
            dueNow = 0;
          } else if (smallThr > 0 && smallFee > 0 && cart.total_price < smallThr) {
            dueNow = smallFee;
          } else {
            dueNow = fee;
          }
          grandTotal    = cart.total_price + dueNow;

          // Free-row visibility, wording and colour all come from theme
          // settings, carried on the row so this path matches the Liquid render.
          var showFree = deliveryRow.dataset.deliveryShowFree !== 'false';
          deliveryRow.hidden = (dueNow === 0 && !showFree);

          var dValueEl  = deliveryRow.querySelector('[data-cart-delivery]');
          if (dValueEl) {
            if (dueNow === 0) {
              var freeLabel = deliveryRow.dataset.deliveryFreeLabel
                || tt('deliveryFree') || 'Free';
              var freeColor = deliveryRow.dataset.deliveryFreeColor || '';
              dValueEl.innerHTML = '<span class="cart-drawer__delivery-free"'
                + (freeColor ? ' style="color: ' + CartManager._escape(freeColor) + '"' : '')
                + '>' + CartManager._escape(freeLabel) + '</span>';
            } else {
              dValueEl.textContent = CartManager._fmt(dueNow);
            }
          }
        }

        // Total (after all discounts, plus delivery when it is shown)
        if (subtotalEl)  subtotalEl.textContent = CartManager._fmt(grandTotal);
        if (checkoutBtn) {
          checkoutBtn.textContent = tt('checkoutWithTotalTpl', { total: CartManager._fmt(grandTotal) })
            || ('Checkout — ' + CartManager._fmt(grandTotal));
        }
      }

      var itemsHtml = '<ul class="cart-items" role="list" data-cart-items>';
      cart.items.forEach(function (item) {
        var imgUrl = item.image ? item.image : (item.featured_image ? item.featured_image.url : null);
        var altText = item.featured_image && item.featured_image.alt ? item.featured_image.alt : item.title;
        var imgTag = '';
        if (imgUrl) {
          var sizedUrl = imgUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_100x100.$1$2');
          imgTag = '<img src="' + sizedUrl + '" class="cart-item__img"'
            + ' alt="' + CartManager._escape(altText) + '"'
            + ' loading="lazy" width="100" height="100">';
        }

        var variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
          ? '<p class="cart-item__variant text-small">' + CartManager._escape(item.variant_title) + '</p>'
          : '';

        var qtyLabel       = tt('qtyLabelTpl', { title: item.title }) || ('Quantity for ' + item.title);
        var decreaseLabel  = tt('decreaseQty') || 'Decrease quantity';
        var increaseLabel  = tt('increaseQty') || 'Increase quantity';
        var removeLabel    = tt('removeItemTpl', { title: item.title }) || ('Remove ' + item.title);
        var removeText     = tt('remove') || 'Remove';

        // Line-level discount visibility: strikethrough original + per-discount tags
        var priceHtml = '';
        if (typeof item.original_line_price === 'number' && item.original_line_price > item.final_line_price) {
          priceHtml += '<s class="cart-item__price-original">' + CartManager._fmt(item.original_line_price) + '</s>';
        }
        priceHtml += CartManager._fmt(item.final_line_price);

        var discountTagsHtml = '';
        var allocs = item.line_level_discount_allocations || [];
        if (allocs.length > 0) {
          discountTagsHtml = '<ul class="cart-item__discounts" role="list">';
          for (var ai = 0; ai < allocs.length; ai++) {
            var alloc = allocs[ai] || {};
            var dApp = alloc.discount_application || {};
            var dTitle = CartManager._escape(dApp.title || dApp.code || 'Discount');
            var dAmount = CartManager._fmt(alloc.amount || 0);
            discountTagsHtml += '<li class="cart-item__discount-tag">'
              +   '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
              +     '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>'
              +     '<line x1="7" y1="7" x2="7.01" y2="7"/>'
              +   '</svg>'
              +   '<span>' + dTitle + ' (−' + dAmount + ')</span>'
              + '</li>';
          }
          discountTagsHtml += '</ul>';
        }

        itemsHtml += '<li class="cart-item" data-cart-item data-item-key="' + item.key + '">'
          + '<a href="' + item.url + '" class="cart-item__img-wrap" tabindex="-1" aria-hidden="true">' + imgTag + '</a>'
          + '<div class="cart-item__info">'
          + '<a href="' + item.url + '" class="cart-item__title">' + CartManager._escape(item.product_title) + '</a>'
          + variantHtml
          + '<p class="cart-item__price-val" style="margin-top:2px">' + priceHtml + '</p>'
          + discountTagsHtml
          + '<div class="cart-item__controls">'
          + '<div class="cart-item__qty" aria-label="' + CartManager._escape(qtyLabel) + '">'
          + '<button class="cart-item__qty-btn" data-qty-change="' + item.key + '" data-qty-delta="-1" aria-label="' + CartManager._escape(decreaseLabel) + '">−</button>'
          + '<span class="cart-item__qty-val" data-qty-display="' + item.key + '">' + item.quantity + '</span>'
          + '<button class="cart-item__qty-btn" data-qty-change="' + item.key + '" data-qty-delta="1" aria-label="' + CartManager._escape(increaseLabel) + '">+</button>'
          + '</div>'
          + '<button class="cart-item__remove text-small" data-remove-item="' + item.key + '" aria-label="' + CartManager._escape(removeLabel) + '">' + CartManager._escape(removeText) + '</button>'
          + '</div>'
          + '</div>'
          + '</li>';
      });
      itemsHtml += '</ul>';

      var existingList  = body.querySelector('[data-cart-items]');
      var existingEmpty = body.querySelector('[data-cart-empty]');
      var existingUpsel = body.querySelector('[data-cart-upsell]');
      var tempDiv = document.createElement('div');
      tempDiv.innerHTML = itemsHtml;
      var newList = tempDiv.firstChild;

      if (existingList) {
        body.replaceChild(newList, existingList);
      } else if (existingEmpty) {
        body.replaceChild(newList, existingEmpty);
      } else {
        body.innerHTML = '';
        body.appendChild(newList);
        if (existingUpsel) body.appendChild(existingUpsel);
      }

      CartManager.updateUpsell(cart);
    },

    updateUpsell: function (cart) {
      var body = this.bodyEl || document.querySelector('[data-cart-drawer-body]');
      if (!body) {
        console.warn('[Lumina Upsell] body element not found, aborting');
        return;
      }
      this.bodyEl = body;

      // ── Pick the source queue ────────────────────────────────────────
      var sourceQueue = null;
      var sourceLabel = '';
      if (this._pendingNextQueue && this._pendingNextQueue.length) {
        sourceQueue = this._pendingNextQueue;
        sourceLabel = 'pendingNextQueue (chain from clicked upsell)';
        this._pendingNextQueue = null;
      } else if (this._upsellSourceQueue && this._upsellSourceQueue.length) {
        sourceQueue = this._upsellSourceQueue;
        sourceLabel = 'cached _upsellSourceQueue';
      } else {
        sourceQueue = this._upsellQueue();
        sourceLabel = 'fresh JSON blob #cart-upsell-data';
      }
      // Always clear pending in case it was an empty array
      this._pendingNextQueue = null;
      this._upsellSourceQueue = (sourceQueue && sourceQueue.length) ? sourceQueue : null;

      if (!sourceQueue || !sourceQueue.length) {
        console.warn('[Lumina Upsell] empty source queue (' + sourceLabel + ') — hiding upsell');
        var existing = body.querySelector('[data-cart-upsell]');
        if (existing) existing.remove();
        this._upsellSourceQueue = null;
        return;
      }

      // ── Filter out anything already in the cart ──────────────────────
      var cartProductIds = new Set();
      var cartVariantIds = new Set();
      (cart.items || []).forEach(function (i) {
        if (i.product_id != null) cartProductIds.add(String(i.product_id));
        if (i.variant_id != null) cartVariantIds.add(String(i.variant_id));
        if (i.id != null) cartVariantIds.add(String(i.id));
      });

      var available = sourceQueue.filter(function (p) {
        var byProduct = p.id != null && cartProductIds.has(String(p.id));
        var byVariant = p.variant_id != null && cartVariantIds.has(String(p.variant_id));
        return !byProduct && !byVariant;
      });
      var visible = available.slice(0, 2);
      var upsellEl = body.querySelector('[data-cart-upsell]');


      if (cart.item_count === 0 || visible.length === 0) {
        if (upsellEl) upsellEl.remove();
        if (cart.item_count === 0) this._upsellSourceQueue = null;
        console.warn('[Lumina Upsell] nothing to show (item_count=' + cart.item_count + ', visible=' + visible.length + ')');
        return;
      }

      var newHtml = this._buildUpsellHTML(visible);
      var tmp = document.createElement('div');
      tmp.innerHTML = newHtml;
      var newNode = tmp.firstChild;

      if (upsellEl) {
        // upsellEl may be nested inside [data-cart-upsell-container] (a
        // grandchild of body), so replace via its actual parent — using
        // body.replaceChild here would throw NotFoundError.
        upsellEl.replaceWith(newNode);
      } else {
        var container = body.querySelector('[data-cart-upsell-container]');
        if (container) {
          container.innerHTML = '';
          container.appendChild(newNode);
        } else {
          body.appendChild(newNode);
        }
      }
    },

    _upsellQueue: function () {
      var script = document.getElementById('cart-upsell-data');
      if (!script) return [];
      var raw = script.textContent || '';
      try { return JSON.parse(raw); }
      catch (e) { console.error('[Lumina Upsell] JSON parse failed:', e); return []; }
    },

    _buildUpsellHTML: function (products) {
      var script        = document.getElementById('cart-upsell-data');
      var fallbackHead  = tt('completeYourRoutine') || 'Complete Your Routine';
      var heading       = script ? (script.dataset.heading || fallbackHead) : fallbackHead;
      var addLabel      = tt('addShort') || '+ Add';
      var viewLabel     = tt('view') || 'View';

      var itemsHtml = products.map(function (p) {
        var imgHtml = p.image
          ? '<img src="' + p.image + '" class="cart-upsell__img" alt="'
            + CartManager._escape(p.title) + '" loading="lazy" width="56" height="56">'
          : '';

        var nextQueueAttr = '';
        if (p.single_variant && p.next_queue && p.next_queue.length) {
          try {
            var nextJson = JSON.stringify(p.next_queue);
            nextQueueAttr = ' data-next-queue=\'' + nextJson.replace(/'/g, '&#39;') + '\'';
          } catch (e) { /* ignore */ }
        }

        var addToCartLabel = tt('addItemToCartTpl', { title: p.title }) || ('Add ' + p.title + ' to cart');
        var btnHtml = p.single_variant
          ? '<button class="cart-upsell__add btn btn-secondary btn-sm"'
            + ' data-quick-add="' + p.variant_id + '"'
            + ' data-product-id="' + p.id + '"'
            + nextQueueAttr
            + ' data-original-text="' + CartManager._escape(addLabel) + '"'
            + ' aria-label="' + CartManager._escape(addToCartLabel) + '">' + CartManager._escape(addLabel) + '</button>'
          : '<a href="' + p.url + '" class="btn btn-secondary btn-sm cart-upsell__add">' + CartManager._escape(viewLabel) + '</a>';

        return '<div class="cart-upsell__item">'
          + '<a href="' + p.url + '" class="cart-upsell__img-link" tabindex="-1" aria-hidden="true">' + imgHtml + '</a>'
          + '<div class="cart-upsell__info">'
          + '<a href="' + p.url + '" class="cart-upsell__title">' + CartManager._escape(p.title) + '</a>'
          + '<p class="cart-upsell__price text-small">' + CartManager._escape(p.price) + '</p>'
          + '</div>'
          + btnHtml
          + '</div>';
      }).join('');

      return '<div class="cart-upsell" data-cart-upsell>'
        + '<p class="cart-upsell__heading text-eyebrow">' + CartManager._escape(heading) + '</p>'
        + '<div class="cart-upsell__grid">' + itemsHtml + '</div>'
        + '</div>';
    },

    _updateShippingBar: function (totalPrice) {
      var bar = document.querySelector('[data-cart-shipping-bar]');
      if (!bar) return;
      var threshold = parseInt(bar.dataset.threshold || '0', 10);
      // 0 = no threshold known for this market; the bar is rendered hidden.
      if (!(threshold > 0)) return;
      var progress  = Math.min(100, Math.round(totalPrice * 100 / threshold));

      var fill = bar.querySelector('.cart-shipping-bar__fill');
      var msg  = bar.querySelector('.cart-shipping-bar__msg');

      if (fill) {
        fill.style.width = progress + '%';
        fill.setAttribute('aria-valuenow', progress);
      }

      if (msg) {
        if (totalPrice >= threshold) {
          msg.innerHTML = tt('freeShippingUnlocked') || '✓ You\'ve unlocked free shipping!';
          msg.classList.add('cart-shipping-bar__msg--achieved');
        } else {
          var remaining = threshold - totalPrice;
          var amount    = '<strong>' + CartManager._fmt(remaining) + '</strong>';
          msg.innerHTML = tt('freeShippingProgressTpl', { amount: amount })
            || ('Spend ' + amount + ' more for free shipping');
          msg.classList.remove('cart-shipping-bar__msg--achieved');
        }
      }
    },

    _emptyStateHTML: function () {
      var emptyMsg     = tt('empty') || 'Your bag is empty';
      var discoverMsg  = tt('discoverProducts') || 'Discover products made for you';
      var continueMsg  = tt('continueShopping') || 'Continue Shopping';
      return '<div class="cart-drawer__empty" data-cart-empty>'
        + '<div class="cart-drawer__empty-icon" aria-hidden="true">'
        + '<svg width="48" height="48" viewBox="0 0 48 48" fill="none">'
        + '<path d="M6 6h4.5L14 33h20l6-18H18" stroke="var(--color-warm-gray-300)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
        + '<circle cx="20" cy="40" r="2.5" fill="var(--color-warm-gray-300)"/>'
        + '<circle cx="32" cy="40" r="2.5" fill="var(--color-warm-gray-300)"/>'
        + '</svg></div>'
        + '<p class="text-heading-3" style="color:var(--color-text-light)">' + CartManager._escape(emptyMsg) + '</p>'
        + '<p class="text-small" style="color:var(--color-text-light)">' + CartManager._escape(discoverMsg) + '</p>'
        + '<button class="btn btn-primary" data-cart-close>' + CartManager._escape(continueMsg) + '</button>'
        + '</div>';
    },

    // Shopify's Ajax Cart API answers 422 with { status, message, description }
    // where description is already in the storefront language ("You can only
    // add 2 of this item to your cart"). Fall back to the generic error.
    errorMessage: function (err) {
      var msg = err && (err.description || err.message);
      if (typeof msg !== 'string' || !msg || msg === 'Failed to fetch') {
        msg = tt('error') || 'Something went wrong. Please try again.';
      }
      return msg;
    },

    showError: function (err) {
      var msg = CartManager.errorMessage(err);
      if (window.Lumina && window.Lumina.toast) {
        window.Lumina.toast.show(msg, 'error');
      }
      return msg;
    },

    _fmt: function (price) {
      try {
        if (window.Lumina && window.Lumina.utils && window.Lumina.utils.formatMoney) {
          return window.Lumina.utils.formatMoney(price);
        }
      } catch (e) {}
      var i18n = getI18N();
      var unit = i18n.currencyUnit || 'AED';
      return unit + ' ' + (price / 100).toFixed(2);
    },

    _escape: function (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  };

  window.Lumina = window.Lumina || {};
  window.Lumina.cart = CartManager;

  // Auto-bootstrap. Was previously called externally; keep self-init so a
  // missing bootstrap call can never silently break the cart again.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { CartManager.init(); });
  } else {
    CartManager.init();
  }

})();
